import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Printer, AlertCircle, ChevronDown, Eye } from 'lucide-react';
import styles from './PublicDailyReport.module.css';
import { API_ROOT, SMIIFY_API } from '../../config/api';
import OwnerClosing from '../../components/OwnerClosing/OwnerClosing';
import DailyReportView from '../../components/DailyReportView/DailyReportView';

// Day-closing page opened from the WhatsApp link: /r/:token (view only, no login)
export default function PublicDailyReport() {
    const { token } = useParams();
    const [report, setReport] = useState(null);
    const [error, setError] = useState('');
    const [showFull, setShowFull] = useState(false);

    useEffect(() => {
        let alive = true;
        // 1) the daily-report router itself (same code as the in-app report and the WhatsApp message)
        // 2) /public/closing-report  3) /public/reports/daily  -> older servers
        const t = encodeURIComponent(token);
        const urls = [`${SMIIFY_API}/daily-report/public/${t}`, `${API_ROOT}/api/v1/public/closing-report/${t}`, `${API_ROOT}/api/v1/public/reports/daily/${t}`];
        const isOurAnswer = async (res) => {
            if (res.ok) return true;
            if (res.status !== 404 && res.status !== 410) return false;
            const j = await res.clone().json().catch(() => null);
            return Boolean(j && j.success === false && /link/i.test(String(j.message || '')));   // "link is invalid / expired" from our controller
        };
        const tryAll = async () => {
            let last = null;
            for (const u of urls) {
                // eslint-disable-next-line no-await-in-loop
                const res = await fetch(u, { cache: 'no-store' }).catch(() => null);
                if (!res) continue;
                last = res;
                // eslint-disable-next-line no-await-in-loop
                if (await isOurAnswer(res)) return res;
            }
            return last || Promise.reject(new Error('Failed to fetch'));
        };
        tryAll()
            .then(async (res) => {
                const json = await res.json().catch(() => ({}));
                if (!res.ok || !json.success) throw new Error(json.message || 'This closing page could not be opened.');
                if (alive) setReport(json.data);
            })
            .catch((e) => alive && setError(e.message === 'Failed to fetch' ? 'Cannot reach the server. Check your internet and try again.' : e.message));
        return () => { alive = false; };
    }, [token]);

    useEffect(() => {
        if (report?.store?.name) document.title = `Day closing, ${report.store.name}`;
    }, [report]);

    if (error) return <div className={styles.state}><AlertCircle size={26} /><strong>Closing page not available</strong><span>{error}</span></div>;
    if (!report) return <div className={styles.state}><Loader2 size={26} className={styles.spin} /><span>Opening the day closing…</span></div>;

    return (
        <div className={styles.page}>
            <div className={styles.inner}>
                <div className={styles.bar}>
                    <span><Eye size={13} /> View only</span>
                    <button type="button" onClick={() => window.print()}><Printer size={14} /> Print / PDF</button>
                </div>

                <OwnerClosing report={report} />

                <button type="button" className={styles.fullBtn} onClick={() => setShowFull((v) => !v)} aria-expanded={showFull}>
                    {showFull ? 'Hide full report' : 'Show full report: every bill, staff and cash entry'}
                    <ChevronDown size={16} className={showFull ? styles.chevOpen : ''} />
                </button>
                {showFull && <div className={styles.full}><DailyReportView report={report} publicMode /></div>}

                {!report.engine && (
                    <p className={styles.valid} style={{ color: '#9b4600' }}>
                        This report came from an older server version, so vendor payments and purchases may be missing. Update the backend and open the link again.
                    </p>
                )}
                <p className={styles.valid}>
                    Updated {new Date(report.generatedAt || Date.now()).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })} ·{' '}
                    Shared from Hirsta. This page works till {new Date(report.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
            </div>
        </div>
    );
}
